import { defineComponent as _defineComponent } from 'vue'
import { Offer, InsuranceKind, NewInsurance, NewInsuranceVariant, InsuranceCoverage, LoanInsurances } from '@/models/simulation/subtypes';
import { computed } from 'vue';
import { debouncedAtInput } from '@/services/utils/BasicUtils';
import { useSimulationStore } from '@/components/calculator/services/SimulationStore';
import { I18NGetter } from '@/services/enumTranslator/I18NGetter';


export default /*#__PURE__*/_defineComponent({
  __name: 'XSellVariantsRadio',
  props: {
    kind: null,
    offer: null,
    print: { type: Boolean, default: false },
    isAdditionalInfoHidden: { type: Boolean, default: false }
  },
  setup(__props: any) {

const props = __props as { kind: InsuranceKind, offer: Offer, print: boolean, isAdditionalInfoHidden: boolean };



const store = useSimulationStore();
const isCredited: boolean = props.offer.creditedCosts.includes(props.kind);
const insuranceKindToLoanInsurancesKey = (kind: InsuranceKind): Uncapitalize<InsuranceKind> & keyof LoanInsurances =>
      (kind.charAt(0).toLowerCase() + kind.slice(1)) as Uncapitalize<InsuranceKind> & keyof LoanInsurances;
const type: keyof LoanInsurances = insuranceKindToLoanInsurancesKey(props.kind);
const xSellProduct: NewInsurance | undefined = props.offer.insurances?.[type];

const getTranslatedMessage = (key: string): string => {
  const formattedKey = key.replace(/\./g, '_');
  return I18NGetter().useInsurenceBackendMessages?.[formattedKey] || key;
};

const translateText = (text: string): string => {
  const percentRegex = /\d{1,2},\d{1,2}% (kwoty kredytu|kwoty i okresu kredytu)/g;
  const durationRegex = /od (\d{1,2})\. miesiąc(?:a|e|y) do końca kredytu/g;
  const customDurationRegex = /od uruchomienia kredytu do uruchomienia kredytu/g;

  const matches = text.match(percentRegex) || [];
  const translatedMatches = matches.map(match => {
    const [numberPart, ...textParts] = match.split(/ (?=\w+)/);
    const textPart = textParts.join(' ');
    const translatedText = getTranslatedMessage(textPart.trim());
    return `${numberPart} ${translatedText}`;
  });

  let translatedText = text;
  matches.forEach((match, index) => {
    translatedText = translatedText.replace(match, translatedMatches[index]);
  });

  translatedText = translatedText.replace(durationRegex, (match, month) => {
    const isSingular = parseInt(month) === 1;
    const templateKey = isSingular
      ? 'od {{count}}_ miesiąca do końca kredytu'
      : 'od {{count}}_ miesięcy do końca kredytu';
    const template = getTranslatedMessage(templateKey);
    return template ? template.replace('{{count}}', month) : match;
  });

  translatedText = translatedText.replace(customDurationRegex, match => {
    const template = getTranslatedMessage('od uruchomienia kredytu do uruchomienia kredytu');
    return template || match;
  });

  return translatedText;
};

const items = xSellProduct?.variants.filter(variant => variant.isVisible !== false).map(variant => ({
  name_pl: variant.name,
  type: variant.id,
  tooltip: variant.description,
  // WARN: I know that this is not the best way to check if the variant is Lendi Care, but I don't have any other data to check this
  // We discussed it within the team and we decided to use this approach for now and will change it later
  badge: props.kind === "RealEstateInsurance" && variant.name.toLowerCase().replace(/\s+/g, '').includes('lendicare')
    ? I18NGetter().useXSellVariantsRadio.ADVANTAGE
    : undefined,
  description: variant.payments
    // here add translations preferably on be
    .map(payment => `${translateText(payment.paymentDescription) || ''} ${translateText(payment.durationDescription || '')}`)
    .join(', '),
})) ?? [];
const isVoluntary: boolean = Boolean(!xSellProduct?.originallyRequired ||
  props.offer.info.voluntaryInsurances?.includes(props.kind));

// Ta flaga mówi o tym, że produkt jest wymagany z banku, nie mozna kupić go poza bankiem
const mandatoryFromBankProductKinds: InsuranceKind[] = ["BridgingInsurance", "LowPersonalShareInsurance",];

const isAnyRequired = computed<boolean>(() =>
  !isVoluntary &&
  !(props.offer.info.xsellInsurances?.includes(props.kind) || mandatoryFromBankProductKinds.includes(props.kind))
);

const isNotIncluded = computed<boolean>(() => !(isCredited || xSellProduct?.required));

const getHintLabel = (): string | undefined => {
  // W przypadku ubezpieczenia niskiego wkładu nie ma sensu pokazywać etykiety.
  // Ubezpieczenie niskiego wkładu jest zawsze wymagane kiedy jest wliczone i go nie ma kiedy nie jest wliczone
  if (props.kind === "LowPersonalShareInsurance") {
    return undefined;
  }
  const REQUIRED_BY_BANK_KINDS: InsuranceKind[] | undefined = [
    ...(props.offer.info.xsellInsurances ?? []), ...mandatoryFromBankProductKinds,
  ];
  const REQUIRED_BY_BANK_FOR_X_SELL: InsuranceKind[] | undefined = props.offer.info.insurancesRequiredFromBankForXsell;

  if (REQUIRED_BY_BANK_KINDS?.includes(props.kind)) {
    return I18NGetter().useXSellVariantsRadio.REQUIRED_BY_BANK;
  } else if (REQUIRED_BY_BANK_FOR_X_SELL?.includes(props.kind)) {
    return I18NGetter().useXSellVariantsRadio.REQUIRED_BY_BANK_FOR_X_SELL;
  } else if (isVoluntary) {
    return I18NGetter().useXSellVariantsRadio.VOLUNTARY;
  } else {
    return I18NGetter().useXSellVariantsRadio.REQUIRED_ANY;
  }
};
const hintLabel: string | undefined = getHintLabel();

const isMandatoryFromBank: boolean =
  props.kind === "LowPersonalShareInsurance" ||
  ((props.offer.info.xsellInsurances?.includes(props.kind) || mandatoryFromBankProductKinds.includes(props.kind)) &&
    !!xSellProduct?.originallyRequired);

const pickedVariant = computed<Nullable<NewInsuranceVariant>>(() =>
  xSellProduct?.variants.filter(variant => variant.isVisible !== false).find(x => x.id === xSellProduct.currentVariantId) ?? null);

const onRequiredChange = debouncedAtInput(async (required: boolean, insuranceId: string) => {
  const loanId = props.offer.info.id;
  if (!store.userInput!.additionalRequests.insuranceDemands) {
    store.userInput!.additionalRequests.insuranceDemands = [];
  }
  if (store.userInput!.additionalRequests.insuranceDemands) {
    const foundDemand = store.userInput!
      .additionalRequests
      .insuranceDemands
      .find(x => x.insuranceId === insuranceId && x.loanId === loanId);
    if (!foundDemand) {
      store.userInput!.additionalRequests.insuranceDemands.push({
        loanId,
        required,
        insuranceId,
        kind: props.kind,
      });
    } else {
      foundDemand.required = required;
    }
    await store.createSimulation(loanId);
  }
}, 800);
const onInsuranceChange = debouncedAtInput(async (insuranceVariantId: string) => {
  const loanId = props.offer.info.id;
  if (store.userInput!.additionalRequests.replacements) {
    const foundReplacement = store.userInput!.additionalRequests
      .replacements
      .insuranceVariantReplacements
      .find(x => x.loanId === loanId && x.insuranceKind === props.kind);
    if (!foundReplacement) {
      store.userInput!.additionalRequests.replacements.insuranceVariantReplacements.push({
        loanId,
        insuranceVariantId,
        insuranceKind: props.kind,
        insuranceId: xSellProduct!.insuranceDefinitionId!,
      });
    } else {
      foundReplacement.insuranceVariantId = insuranceVariantId;
    }
    await store.createSimulation(loanId);
  }
}, 1000);

const getCoverName = (key: keyof InsuranceCoverage): string => {
  const uppercaseKey = key.toUpperCase() as Uppercase<keyof InsuranceCoverage>;
  return I18NGetter().useXSellVariantsRadio[uppercaseKey];
};

return { __sfc: true,props, store, isCredited, insuranceKindToLoanInsurancesKey, type, xSellProduct, getTranslatedMessage, translateText, items, isVoluntary, mandatoryFromBankProductKinds, isAnyRequired, isNotIncluded, getHintLabel, hintLabel, isMandatoryFromBank, pickedVariant, onRequiredChange, onInsuranceChange, getCoverName, I18NGetter }
}

})